
import { Type } from "@google/genai";
import { ArchiveFile, SearchResult, AIOverview, ChatMessage, DBRow } from "../types";
import { BUCKET_BASE_URL } from "../constants";
import { auth } from "./auth";

// Safe JSON stringify to handle circular references
const safeStringify = (obj: any) => {
  const cache = new Set();
  return JSON.stringify(obj, (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (cache.has(value)) {
        return;
      }
      cache.add(value);
    }
    return value;
  });
};

// Safe helper to check Content-Type and content to prevent JSON parsing errors when static routing fallbacks are returned
const fetchJsonSafely = async (response: Response): Promise<any> => {
  const contentType = response.headers.get("Content-Type");
  if (contentType && contentType.includes("text/html")) {
    throw new Error("API backend is offline or static-hosting fallback active. Please make sure that your custom Express server is deployed and running on a server platform (e.g. Google Cloud Run) rather than just static hosting (e.g. Firebase Hosting or GitHub Pages) to process API calls correctly.");
  }
  
  const text = await response.text();
  if (text.trim().startsWith("<!DOCTYPE") || text.trim().startsWith("<html") || text.trim().startsWith("<!doctype")) {
    throw new Error("API backend is offline or static-hosting fallback active. Please make sure that your custom Express server is deployed and running on a server platform (e.g. Google Cloud Run) rather than just static hosting (e.g. Firebase Hosting or GitHub Pages) to process API calls correctly.");
  }
  
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error("Failed to parse server response as JSON. The backend may have returned an unexpected format.");
  }
};

// Helper to call GenAI via the server proxy
const generateContentServer = async (params: {
  model?: string;
  contents: any;
  systemInstruction?: string;
  responseMimeType?: string;
  responseSchema?: any;
  temperature?: number;
  topP?: number;
  topK?: number;
  maxOutputTokens?: number;
}) => {
  try {
    const user = auth?.currentUser;
    if (!user) throw new Error("Please log in to use AI features.");

    const idToken = await user.getIdToken();
    const response = await fetch("/api/ai/generate", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify(params)
    });

    if (!response.ok) {
        const errorData = await fetchJsonSafely(response).catch(() => ({}));
        if (response.status === 402) {
            throw new Error(`QUOTA_EXCEEDED: ${errorData.message || "Daily limit reached."}`);
        }
        throw new Error(errorData.error || `Server Error: ${response.status}`);
    }

    const data = await fetchJsonSafely(response);
    const text = data.text;

    if (params.responseMimeType === "application/json") {
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error("Failed to parse AI JSON response:", text);
        throw new Error("AI returned invalid JSON format");
      }
    }
    
    return text;
  } catch (error: any) {
    if (error.message && error.message.startsWith("QUOTA_EXCEEDED")) throw error;
    console.error("Gemini Server Error:", error);
    throw error;
  }
};

// Helper to call GenAI directly - COMPATIBILITY WRAPPER
/** @deprecated Use generateContentServer */
const generateContentDirect = generateContentServer;

// Helper to map models to recommended versions
const getTargetModel = (model?: string) => {
    let targetModel = model || "gemini-flash-latest";
    if (targetModel === "gemini-1.5-flash") return "gemini-flash-latest";
    if (targetModel === "gemini-1.5-pro") return "gemini-3.1-pro-preview";
    return targetModel;
};

// --- INSTRUCTIONS ---

const EDITOR_SYSTEM_INSTRUCTION = `
You are the Cayce Archive Librarian and Chief Editor. 
Your goal is to assist in the creation, curation, and formatting of the Akashic Wiki.

You are working with a structured list of "Editor Blocks". The article is composed of a sequence of these blocks.

**Block Types:**
- **'text'**: Contains HTML content. Supported tags: <p>, <h1>, <h2>, <h3>, <ul>, <ol>, <li>, <blockquote>, <table>, <tr>, <td>, <th>.
- **'image'**: Contains a direct image URL (e.g., Unsplash).
- **'video'**: Contains a video URL (e.g., YouTube embed URL).
- **'divider'**: Represents a horizontal separator.
- **'gallery'**: Contains a JSON array of image URLs as a string (e.g., '["url1", "url2"]'). Use this for image carousels or grids.

**Key Capabilities & Rules:**
1. **Direct Editing**: You receive the current list of blocks. You MUST return the *complete* new list of blocks to reflect requested changes.
2. **Insertion**: To add content, insert a new block at the appropriate index in the array.
3. **Splitting Text**: If the user wants to insert an image or video *inside* a long text paragraph, you must SPLIT that 'text' block into two separate 'text' blocks and place the new 'image'/'video' block in between them.
4. **Formatting**: Use HTML tags within 'text' blocks for formatting (bold <b>, italic <i>, etc.).
5. **Scholarly Tone**: Maintain an objective, mystical, and scholarly tone appropriate for the Edgar Cayce readings.
6. **Images**: If asked to add images, insert an 'image' block with a relevant keyword-based URL from Unsplash (e.g., "https://source.unsplash.com/1600x900/?egypt,pyramid").
7. **Galleries**: If asked for multiple images or a gallery, insert a 'gallery' block with a stringified JSON array of Unsplash URLs.

**Constraints:**
- **Preserve IDs**: If a block is unchanged, keep its original 'id'. If creating a new block, generate a new unique string ID (e.g., "b-ai-${Date.now()}").
- **Preserve Content**: Do NOT lose existing text unless explicitly asked to remove or summarize it.
- **Special Markers**: Respect any content marked with "<<EXISTING_IMAGE_DATA_DO_NOT_MODIFY>>".
- The 'summary' field should be a concise chat message explaining what you did.
`;

const CHAT_SYSTEM_INSTRUCTION = `
You are the Cayce Archive Librarian.
Your goal is to answer user questions by synthesizing wisdom from the Edgar Cayce readings.

CRITICAL MANDATORY RULES FOR ALL RESPONSES:
1. **AT LEAST TWO VERBATIM QUOTES**: In EVERY response, you MUST ALWAYS include AT LEAST TWO (2) verbatim, word-for-word quotes directly from the Edgar Cayce readings provided in the RETRIEVED CONTEXT. Format each quote prominently using blockquotes (> "verbatim quote...") or italicized inline quotes.
2. **100% VERBATIM REQUIREMENT**: All quotes from the readings MUST be 100% verbatim, word-for-word copies of the text from the 'RETRIEVED CONTEXT' section provided. DO NOT paraphrase, summarize, or invent quotes.
3. **HYPERLINKED CITATION SYNTAX**: Immediately following each verbatim quote, you MUST include a clickable citation link using this EXACT format:
   [[Reading ID | "exact highlight text or paragraph bookmark"]]
   
   - The 'Reading ID' MUST match the document ID provided in the context (e.g., 'Reading 262-60' or '262-60').
   - The text INSIDE the quotes (the highlight target) MUST be:
     a) Preferred: The specific paragraph bookmark / section number if citing a whole or major part of a numbered paragraph (e.g., "(1)", "1.", "(2)", etc.).
     b) A short verbatim substring (3-8 words) of the exact beginning of the cited sentence. It CANNOT contain ellipses.
   - **Examples**:
     - Numbered paragraph: > "The one substance vibrates in different dynamic degrees..." [[Reading 195-70 | "(1)"]]
     - Sentence quote: As Cayce emphasized, > "Mind is indeed the builder; what one thinks upon, that one becomes." [[Reading 262-60 | "Mind is indeed the builder"]]

4. **Interpretation & Insight**: Draw broad, creative, and insightful comparisons between the user's question/situation and the universal principles found in the readings. Connect the dots between modern life and archival wisdom.
5. **Tone**: Conversational, scholarly, empathetic, and wise.
6. **Format**: Use Markdown (bold headers, lists, blockquotes).
7. **Language**: Match the user's language.
`;

export interface EditorBlockData {
  id: string;
  type: 'text' | 'image' | 'divider' | 'video' | 'gallery';
  content: string;
}

const getFriendlyErrorMessage = (error: any): string => {
    const msg = (error.message || error.toString()).toLowerCase();
    if (msg.includes("unexpected token") || msg.includes("is not valid json") || msg.includes("static-hosting fallback") || msg.includes("api backend is offline")) {
        return "System Alert: The application backend is currently offline, or static-hosting fallback is active on this custom domain. Please make sure that your custom Express server is deployed and running on a server platform (e.g. Google Cloud Run) rather than just static hosting (e.g. Firebase Hosting or GitHub Pages) to process API calls correctly.";
    }
    if (msg.includes("payment method") || msg.includes("account settings") || msg.includes("monthly usage limit")) {
        return error.message;
    }
    if (msg.includes("api key expired")) {
        return "System Alert: The API Key for the Akashic Records has expired. Please update the environment configuration.";
    }
    if (msg.includes("service account") || msg.includes("bind it to a service account")) {
        return "System Alert: Your API Key has Google Cloud restrictions that require it to be bound to a service account. Please use a standard API Key from Google AI Studio (aistudio.google.com) or remove 'Vertex AI' restrictions from your Google Cloud key.";
    }
    if (msg.includes("api key not valid") || msg.includes("api_key_invalid") || msg.includes("invalid api key")) {
        return `System Alert: The provided API Key is invalid or not authorized. Please check your configuration in the server environment.`;
    }
    if (msg.includes("referer") || msg.includes("referrer")) {
        return "System Alert: This API Key is restricted by HTTP Referrer. Please ensure this domain is allowed in your Google Cloud Console API Key settings.";
    }
    if (msg.includes("quota") || msg.includes("resource exhausted")) {
        return "System Alert: Usage limits exceeded. Please try again later.";
    }
    return `Connection unstable. Details: ${error.message || "Unknown Error"}`;
};


export const editArticleWithAI = async (
  query: string,
  currentBlocks: EditorBlockData[],
  fileInfo?: ArchiveFile,
  localDB?: Map<string, DBRow>
): Promise<{ summary: string; blocks: EditorBlockData[] }> => {
  try {
    const user = auth?.currentUser;
    if (!user) throw new Error("Please log in to use AI features.");

    const idToken = await user.getIdToken();
    const response = await fetch("/api/ai/edit-article", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({
            query,
            currentBlocks,
            fileInfo
        })
    });

    if (!response.ok) {
        const errorData = await fetchJsonSafely(response).catch(() => ({}));
        throw new Error(errorData.error || `Server Error: ${response.status}`);
    }

    const result = await fetchJsonSafely(response);

    if (!result.blocks || !Array.isArray(result.blocks)) {
        return { 
            summary: "I analyzed the text but couldn't restructure it safely. Here is a text response: " + (result.summary || "No changes."),
            blocks: currentBlocks 
        };
    }

    return { summary: result.summary, blocks: result.blocks };

  } catch (error: any) {
    console.error("AI Edit Error:", error.message || error);
    return {
        summary: getFriendlyErrorMessage(error),
        blocks: currentBlocks
    };
  }
};

export const generateAIOverview = async (
  query: string, 
  results: SearchResult[], 
  previousHistory: ChatMessage[] = []
): Promise<AIOverview> => {
  try {
    const historyString = previousHistory.map(m => `${m.role}: ${m.text}`).join('\n');
    const parsed = await generateContentDirect({
      model: "gemini-flash-latest",
      contents: [{
        role: "user",
        parts: [{ text: `Context: ${historyString}\n\nQuery: "${query}"\n\nGenerate a structured research overview based on the retrieved data.` }]
      }],
      systemInstruction: CHAT_SYSTEM_INSTRUCTION,
      responseMimeType: "application/json",
      responseSchema: {
        type: Type.OBJECT,
        properties: {
          text: { type: Type.STRING },
          narrowingQuestion: { type: Type.STRING },
          options: { type: Type.ARRAY, items: { type: Type.STRING } },
          citations: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                title: { type: Type.STRING },
                path: { type: Type.STRING },
                index: { type: Type.NUMBER }
              },
              required: ["title", "path", "index"]
            }
          }
        },
        required: ["text", "narrowingQuestion", "options", "citations"]
      }
    });

    return {
      history: [...previousHistory, { role: 'ai', text: parsed.text }],
      narrowingQuestion: parsed.narrowingQuestion,
      options: parsed.options,
      citations: parsed.citations
    };
  } catch (error: any) {
    console.error("AI Overview Error:", error.message || error);
    return { 
      history: [...previousHistory, { role: 'ai', text: getFriendlyErrorMessage(error) }],
      narrowingQuestion: "System Status", 
      options: ["Retry Connection", "Browse Offline", "Check Settings"], 
      citations: [] 
    };
  }
};

export const searchArchives = async (query: string): Promise<SearchResult[]> => {
  try {
    const user = auth?.currentUser;
    if (!user) throw new Error("Please log in to search records.");

    const idToken = await user.getIdToken();
    const response = await fetch(`/api/ai/search-archives?query=${encodeURIComponent(query)}`, {
        headers: {
            "Authorization": `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
    }

    return await fetchJsonSafely(response);
  } catch (error: any) {
    console.error("Search failed:", error.message || error);
    return [];
  }
};

export const chatWithArchive = async (query: string, history: ChatMessage[] = [], localDB?: Map<string, DBRow>, file?: ArchiveFile, currentContent?: string): Promise<string> => {
  try {
    const user = auth?.currentUser;
    if (!user) throw new Error("Please log in to use AI features.");

    const idToken = await user.getIdToken();
    const response = await fetch("/api/ai/chat", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${idToken}`
        },
        body: JSON.stringify({
            query,
            history,
            currentContent,
            file,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Zurich"
        })
    });

    if (!response.ok) {
        const errorData = await fetchJsonSafely(response).catch(() => ({}));
        throw new Error(errorData.error || `Server Error: ${response.status}`);
    }

    const data = await fetchJsonSafely(response);
    return data.text || "I was unable to find an answer.";
  } catch (error: any) {
    console.error("Gemini Chat Error:", error.message || error);
    return getFriendlyErrorMessage(error);
  }
};

const _unused_chatWithArchive = async (query: string, history: ChatMessage[] = [], localDB: Map<string, DBRow>, file?: ArchiveFile, currentContent?: string): Promise<string> => {
  let safeContent = currentContent || "";
  if (safeContent.length > 50000) {
      safeContent = safeContent.replace(/src="data:image\/[^"]+"/g, 'src="<<SKIPPED_IMAGE_DATA>>"');
  }

  let retrievedContext = '';
  if (localDB.size > 0 && !file && !currentContent) {
      const fullQueryText = [
          ...history.filter(m => m.role === 'user').map(m => m.text),
          query
      ].join(' ');
      
      const allTerms = fullQueryText.toLowerCase().trim().split(/\s+/);
      const stopWords = new Set(['the', 'a', 'an', 'is', 'in', 'on', 'of', 'for', 'to', 'and', 'what', 'where', 'who', 'why', 'how', 'it', 'did', 'do', 'does', 'about', 'me', 'you', 'my', 'your', 'tell']);
      const meaningfulTerms = allTerms.filter(t => t.length > 2 && !stopWords.has(t));
      const uniqueTerms = [...new Set(meaningfulTerms)];

      const readingIdPattern = /(\d+)-(\d+)/g;
      const mentionedReadingIds: string[] = [];
      let match;
      while ((match = readingIdPattern.exec(fullQueryText)) !== null) {
          mentionedReadingIds.push(match[0]);
          const caseNum = match[1].padStart(4, '0');
          const readingNum = match[2].padStart(4, '0');
          mentionedReadingIds.push(`${caseNum}-${readingNum}`);
      }

      if (uniqueTerms.length > 0 || mentionedReadingIds.length > 0) {
          const results: {row: DBRow, score: number}[] = [];
          
          localDB.forEach((row) => {
              const nameLower = row.name.toLowerCase();
              const contentLower = row.content.toLowerCase();
              let score = 0;
              let matches = 0;

              mentionedReadingIds.forEach(id => {
                  if (nameLower.includes(id.toLowerCase()) || row.id.toLowerCase().includes(id.toLowerCase())) {
                      score += 200;
                      matches++;
                  }
              });

              uniqueTerms.forEach(term => {
                  if (nameLower.includes(term)) { score += 10; matches++; }
                  if (contentLower.includes(term)) { score += 1; matches++; }
              });

              if (matches > 0) {
                  if (contentLower.includes(query.toLowerCase())) { score += 20; }
                  results.push({ row, score });
              }
          });

          const topResults = results.sort((a, b) => b.score - a.score).slice(0, 8);
          if (topResults.length > 0) {
              retrievedContext = topResults.map(res => 
                  `--- START OF DOCUMENT (ID: ${res.row.name}) ---\n${res.row.content}\n--- END OF DOCUMENT (ID: ${res.row.name}) ---\n\n`
              ).join('');
          }
      }
  }

  let systemInstruction = CHAT_SYSTEM_INSTRUCTION;
  if (retrievedContext) {
      systemInstruction += `\n\nRETRIEVED CONTEXT FOR THIS QUERY:\n"""\n${retrievedContext}\n"""`;
  } else if (safeContent) {
      systemInstruction += `\n\nCurrent Working Document Context:\n"""${safeContent}"""`;
  } else if (file) {
      systemInstruction += `\n\nSpecific Reference Document Context: ${file.name} (${file.category})`;
  }

  const recentHistory = history.slice(-20).filter(msg => msg.text && msg.text.trim().length > 0);
  const contents: any[] = recentHistory.map(msg => ({
      role: msg.role === 'user' ? 'user' : 'model',
      parts: [{ text: msg.text }]
  }));

  contents.push({
      role: 'user',
      parts: [{ text: query }]
  });

  try {
    const result = await generateContentDirect({
        model: "gemini-flash-latest",
        contents: contents,
        systemInstruction: systemInstruction 
    });
    return result || "Synchronizing with index...";
  } catch (error: any) {
    console.error("Gemini Chat Error:", error.message || error);
    return getFriendlyErrorMessage(error);
  }
};

export const summarizeArchiveFile = async (file: ArchiveFile, fileContent?: string): Promise<{summary: string; suggestedFollowUps: string[]}> => {
  const prompt = fileContent 
    ? `Analyze the following document content for "${file.name}":\n\n${fileContent.substring(0, 50000)}\n\nSummarize key findings and suggest follow-ups.` 
    : `Analyze this document path: ${file.path}. Summarize key findings and suggest follow-ups.`;

  try {
      const result = await generateContentDirect({
        model: "gemini-flash-latest",
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            summary: { type: Type.STRING },
            suggestedFollowUps: { type: Type.ARRAY, items: { type: Type.STRING } }
          },
          required: ["summary", "suggestedFollowUps"]
        }
      });
      return result;
  } catch (e) {
      console.error("Summarize failed", e);
      return { summary: "Could not generate summary.", suggestedFollowUps: [] };
  }
};

export const generateChatTitle = async (history: ChatMessage[]): Promise<string> => {
  if (history.length === 0) return "New Chat";
  const conversationStart = history.slice(0, 2).map(m => `${m.role === 'user' ? 'User' : 'AI'}: ${m.text}`).join('\n');
  try {
    const result = await generateContentDirect({
      model: "gemini-flash-latest",
      contents: [{ role: "user", parts: [{ text: `Based on the following conversation, generate a concise and fitting title (maximum 5 words).\n\nConversation:\n${conversationStart}` }] }]
    });
    const title = result?.replace(/"/g, '').trim();
    return title && title.length > 0 ? title : "Chat Discussion";
  } catch (error) {
    const firstUserMessage = history.find(m => m.role === 'user')?.text || "Chat Discussion";
    return firstUserMessage.length > 30 ? firstUserMessage.substring(0, 27) + "..." : firstUserMessage;
  }
};
